'use client'

import DashboardContentWrapper from '@/shared/components/dashboard-content-wrapper'
import Heading from '@/shared/components/ui/typography/heading'
import LocationSelector from '@/shared/components/location-selector'
import VolunteerTabs from '@/features/volunteers/components/VolunteerTabs'
import FilterSearchVolunteersTab from '@/features/volunteers/components/FilterSearchVolunteersTab'
import { ArTeamCalendarTab } from '@/features/volunteers/components/ArTeamCalendarTab'
import { CalendarGrid } from '@/features/stm/components/calendar/CalendarGrid'
import { DashboardStats } from '@/features/stm/components/dashboard/DashboardStats'
import { StmGroupsTab } from '@/features/stm/components/StmGroupsTab'
import { GroupFormDialog } from '@/features/stm/components/forms/GroupFormDialog'
import { useSession } from '@/features/auth/context/session.context'
import { useIsGroupContact } from '@/features/stm/hooks/use-is-group-contact'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { UserRole } from '@/shared/constants/user-roles'
import { useRouter, useSearchParams } from 'next/navigation'
import { buildRegionUrl } from '@/shared/lib/utils'
import {
  SEARCH_PARAMS_RETURN_GROUP_ID_KEY,
  SEARCH_PARAMS_TAB_KEY,
  SEARCH_PARAMS_WEEK_START_KEY,
  VOLUNTEER_TAB_FILTER_SEARCH,
  VOLUNTEER_DEFAULT_TAB_WHEN_STM,
  STM_TAB_VALUES,
  AR_TEAM_TAB_VALUE,
  isStmTabsVisibleForRegion,
  type VolunteerTabValue,
} from '@/features/volunteers/constants/volunteer-tab-links'
import { UrlSearchParams } from '@/shared/types'
import React, { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getDefaultVolunteerTableRowsParams } from '@/features/volunteers/hooks/volunteers.hooks'
import { getVolunteerTableRows, getArLeadershipDates } from '@/features/volunteers/actions/volunteers.actions'
import { ADMIN } from '@/shared/constants/backend-api'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'

function VolunteersPage() {
  const [addGroupFormOpen, setAddGroupFormOpen] = useState(false)
  const { session, isLoading: sessionLoading } = useSession()
  const { regionId, regionSlug, isLoading: regionIdLoading } = useRegionId()
  const router = useRouter()
  const searchParamsSync = useSearchParams()
  const queryClient = useQueryClient()
  const weekStartFromUrl = searchParamsSync.get(SEARCH_PARAMS_WEEK_START_KEY)
  const returnGroupIdFromUrl = searchParamsSync.get(SEARCH_PARAMS_RETURN_GROUP_ID_KEY)
  const groupsWeekStartContext = useMemo(() => {
    if (!weekStartFromUrl) return undefined
    return /^\d{4}-\d{2}-\d{2}$/.test(weekStartFromUrl) ? weekStartFromUrl : undefined
  }, [weekStartFromUrl])
  const groupsReturnToGroupId = useMemo(() => {
    if (!returnGroupIdFromUrl) return undefined
    return returnGroupIdFromUrl.trim() || undefined
  }, [returnGroupIdFromUrl])

  const canManageVolunteers =
    session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
  const isGroupContactRole =
    session?.role === UserRole.VOLUNTEER ||
    session?.role === UserRole.TEAM_LEADER ||
    session?.role === UserRole.PARTNER
  const { isGroupContact, inAnyGroup, isLoading: isGroupContactLoading } = useIsGroupContact()
  /** V/TL/P who are in any group (contact or member) see the simplified groups-only view */
  const showSimplifiedGroupsView = isGroupContactRole && inAnyGroup

  const showStmTabs =
    (session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER) &&
    isStmTabsVisibleForRegion(regionId ?? undefined)
  const showArTeamTab =
    (session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER) &&
    regionId != null
  const isAdmin = canManageVolunteers

  // Parse tab synchronously from URL
  const tabFromUrl = useMemo(() => {
    const tabParam = searchParamsSync.get(SEARCH_PARAMS_TAB_KEY)
    return tabParam || null
  }, [searchParamsSync])

  // Default tab when URL has no ?tab= : Calendar when STM tabs visible, else AR Team, else Filter/Search
  const defaultTab: VolunteerTabValue = useMemo(
    () =>
      showStmTabs
        ? VOLUNTEER_DEFAULT_TAB_WHEN_STM
        : showArTeamTab
          ? AR_TEAM_TAB_VALUE
          : VOLUNTEER_TAB_FILTER_SEARCH,
    [showStmTabs, showArTeamTab]
  )

  // Determine effective tab
  // Don't check showStmTabs/showArTeamTab here - we'll handle redirects in useEffect
  const effectiveTab: VolunteerTabValue = useMemo(() => {
    if (!tabFromUrl) {
      return defaultTab
    }

    if (STM_TAB_VALUES.includes(tabFromUrl as any)) {
      return tabFromUrl as VolunteerTabValue
    }

    if (tabFromUrl === AR_TEAM_TAB_VALUE) {
      return AR_TEAM_TAB_VALUE
    }

    if (tabFromUrl === VOLUNTEER_TAB_FILTER_SEARCH) {
      return VOLUNTEER_TAB_FILTER_SEARCH
    }

    return defaultTab
  }, [tabFromUrl, defaultTab])

  // Prefetch filter-search table rows and AR Team data when Volunteers section mounts so first click is often instant
  useEffect(() => {
    if (showSimplifiedGroupsView) return
    const params = getDefaultVolunteerTableRowsParams()
    queryClient.prefetchQuery({
      queryKey: [ADMIN.tableRows, params],
      queryFn: () => getVolunteerTableRows(params),
      staleTime: 2 * 60 * 1000,
    })
    if (showArTeamTab && regionId != null && regionId > 0) {
      queryClient.prefetchQuery({
        queryKey: [ADMIN.tableRows, 'ar-leadership', regionId],
        queryFn: () => getArLeadershipDates(regionId ?? undefined),
        staleTime: 2 * 60 * 1000,
      })
    }
  }, [regionId, showSimplifiedGroupsView, showArTeamTab, queryClient])

  // When no tab in URL and user has Calendar/AR Team, set URL to default tab so address bar matches content
  useEffect(() => {
    if (sessionLoading || regionIdLoading) return
    if (regionSlug && regionId === null) return
    if (tabFromUrl != null) return
    if (!showStmTabs && !showArTeamTab && !showSimplifiedGroupsView) return

    const redirectTab = showStmTabs
      ? VOLUNTEER_DEFAULT_TAB_WHEN_STM
      : showSimplifiedGroupsView
        ? 'groups'
        : AR_TEAM_TAB_VALUE
    const debugParam = searchParamsSync.get('debug') ?? searchParamsSync.get('Debug')
    const params: Record<string, string> = { [SEARCH_PARAMS_TAB_KEY]: redirectTab }
    if (debugParam) params.debug = debugParam
    const href = regionSlug
      ? buildRegionUrl(regionSlug, '/volunteers', params)
      : `/volunteers?${new URLSearchParams(params).toString()}`
    router.replace(href)
  }, [tabFromUrl, showStmTabs, showArTeamTab, showSimplifiedGroupsView, sessionLoading, regionIdLoading, regionSlug, regionId, router, searchParamsSync])

  // Handle redirect if user tries to access STM or AR Team tab but doesn't have permission
  useEffect(() => {
    if (sessionLoading || regionIdLoading) return
    if (regionSlug && regionId === null) return

    const needsRedirect =
      tabFromUrl &&
      ((STM_TAB_VALUES.includes(tabFromUrl as any) && !showStmTabs && !showSimplifiedGroupsView) ||
        (tabFromUrl === AR_TEAM_TAB_VALUE && !showArTeamTab))

    if (needsRedirect) {
      const redirectTab = showStmTabs
        ? VOLUNTEER_DEFAULT_TAB_WHEN_STM
        : showArTeamTab
          ? AR_TEAM_TAB_VALUE
          : showSimplifiedGroupsView
            ? 'groups'
            : VOLUNTEER_TAB_FILTER_SEARCH
      const debugParam = searchParamsSync.get('debug')
      const params: Record<string, string> = { [SEARCH_PARAMS_TAB_KEY]: redirectTab }
      if (debugParam) params.debug = debugParam
      const href = regionSlug
        ? buildRegionUrl(regionSlug, '/volunteers', params)
        : `/volunteers?${new URLSearchParams(params).toString()}`
      router.replace(href)
    }
  }, [tabFromUrl, showStmTabs, showArTeamTab, showSimplifiedGroupsView, sessionLoading, regionIdLoading, regionSlug, regionId, router, searchParamsSync])

  const showRegionSelector = effectiveTab !== VOLUNTEER_TAB_FILTER_SEARCH

  // While checking if V/TL/P is a group contact, show loading to avoid Filter/Search flash
  if (isGroupContactRole && isGroupContactLoading) {
    return (
      <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
        <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
          <Heading>Volunteers</Heading>
          {showRegionSelector && <LocationSelector />}
        </div>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </DashboardContentWrapper>
    )
  }

  return (
    <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
        <Heading>Volunteers</Heading>
        {showRegionSelector && <LocationSelector />}
      </div>
      {!showSimplifiedGroupsView && (
        <VolunteerTabs tab={effectiveTab} showStmTabs={!!showStmTabs} showArTeamTab={!!showArTeamTab} />
      )}

      {showSimplifiedGroupsView && (
        <StmGroupsTab
          regionId={regionId ?? undefined}
          weekStartContext={groupsWeekStartContext}
          returnToGroupId={groupsReturnToGroupId}
          isGroupContactView
          currentUserId={session?.sub ? Number(session.sub) : undefined}
          currentUserRole={session?.role}
        />
      )}

      {effectiveTab === VOLUNTEER_TAB_FILTER_SEARCH && !showSimplifiedGroupsView && (
        <FilterSearchVolunteersTab canManageVolunteers={!!canManageVolunteers} />
      )}

      {showStmTabs && effectiveTab === 'calendar' && (
        <CalendarGrid
          isAdmin={!!isAdmin}
          regionId={regionId ?? undefined}
          onOpenAddGroup={() => setAddGroupFormOpen(true)}
        />
      )}
      {showStmTabs && effectiveTab === 'dashboard' && (
        <DashboardStats
          onNavigateToGroups={() =>
            router.replace(
              buildRegionUrl(regionSlug || '', '/volunteers', {
                [SEARCH_PARAMS_TAB_KEY]: 'groups',
              })
            )
          }
          onNavigateToGroup={(groupId) => {
            const href = regionSlug
              ? buildRegionUrl(regionSlug, `/volunteers/groups/${groupId}`, { from: 'dashboard' })
              : `/volunteers/groups/${groupId}?from=dashboard`
            router.push(href)
          }}
          onNavigateToCalendarWeek={(weekStart) =>
            router.replace(
              buildRegionUrl(regionSlug || '', '/volunteers', {
                [SEARCH_PARAMS_TAB_KEY]: 'calendar',
                weekStart,
              })
            )
          }
          regionId={regionId ?? undefined}
        />
      )}
      {showStmTabs && effectiveTab === 'groups' && !showSimplifiedGroupsView && (
        <StmGroupsTab
          regionId={regionId ?? undefined}
          weekStartContext={groupsWeekStartContext}
          returnToGroupId={groupsReturnToGroupId}
        />
      )}
      {showArTeamTab && effectiveTab === AR_TEAM_TAB_VALUE && (
        <ArTeamCalendarTab regionId={regionId ?? undefined} />
      )}

      {(showStmTabs || showSimplifiedGroupsView) && (
        <GroupFormDialog
          open={addGroupFormOpen}
          onOpenChange={setAddGroupFormOpen}
          regionId={regionId ?? undefined}
        />
      )}

      <ScrollToTopFab />
    </DashboardContentWrapper>
  )
}

export default VolunteersPage
